<script lang="ts">
    import CardTags from "./CardTags.svelte";
    import { disabled } from "$/utils/disabled";
    import { PreferencesStore } from "$/stores/global/PreferencesStore";
    import Comment from "$/components/Comment.svelte";
    import Editor from "$/components/editor/Editor.svelte";
    import CardHeader from "./CardHeader/CardHeader.svelte";
    import CardTitle from "./CardTitle/CardTitle.svelte";
    import CardAuthor from "./CardAuthor.svelte";
    import CardReactions from "./CardReactions.svelte";
    import CardContent from "./CardContent/CardContent.svelte";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { TokenStore } from "$/stores/TokenStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { SelectedSnippetsStore } from "$/stores/SelectedSnippetsStore.js";
    import { snippetSidePanelStore } from "$/stores/snippetSidePanelStore";
    import { TenantStore } from "$/stores/TenantStore";
    import {
        AppStore,
        AppType,
        focusedCommentId,
        focusedSnippetId,
        Panels,
    } from "$/stores/AppStore";
    import { Kindle } from "$/utils/Kindle";
    import { highlightTarget } from "$/utils/highlightTarget";
    import { formattedDateAndTime, fromNow } from "$/utils/date";
    import { tooltip } from "$/utils/tooltip";
    import {
        generateUrlFromSnippet,
        stripHtml,
        copyToClip,
        isSocialMediaLink,
        trimHTMLText,
        checkURLMatch,
        generateUrlFromSnippetForExtension,
        isExtensionOrPdfViewer,
        isSocialMediaSnippet,
        clearUrlByMarker,
        isPdfMissing,
        removeUrlParameters,
        isRedditPage,
    } from "$/utils/Utils";
    import { isMobile } from "$/utils/isMobile";
    import { clickAction } from "$/utils/clickAction";
    import Collapsible from "../Collapsible.svelte";
    import {
        highlightAndScrollToSnippet,
        scrollToSnippet,
        findSnippet,
    } from "$/utils/CustomLocateV4/locator";
    import { onMount } from "svelte";
    import { cleanupByHighlightId } from "$/utils/CustomLocateV4/marker/utils";
    import { HighlightStore } from "$/stores/HighlightStore";
    import { ExtensionStore } from "$/stores/ExtensionStore";
    import { get } from "svelte/store";
    import CardDialog from "$/components/CardDialog.svelte";

    export let card;
    export let openInNewTab = false;
    export let print = false;
    export let isGridCard = false;
    export let editRole: TopicAccessLevel = TopicAccessLevel.OWNER;
    export let ordinal = 0;
    export let openSearch = false;
    export let topicId;
    export let hideReactions = false;
    export let isCopilot = false;
    export let addCopilotReaction = (reaction, snippet) => {};
    export let deleteSnippet = (snippetId, topicId) => {};
    export let createComment = (comment, snippetId, topicId): any => {};
    export let updateComment = (comment, snippetId, topicId): any => {};
    export let deleteComment = (comment, snippetId, topicId) => {};
    export let user;
    export let bgColor = "white";
    // TECH sm, md media queries can't be used because extension acts as a mobile on big screens
    export let isExtension = false;
    export let isExpanded = false;

    let downloadKindleShowModal = false;
    let shouldShowKindleModal = true;
    let editor;
    let cardTitleRef;
    let showInput = false;
    let commentInputValue = "";
    let editorReferenceElement;
    let cardContentWrapperHeight;
    let isKindleUrl =
        card?.url?.includes("kindle://book/") ||
        card?.url?.includes("kindle://book?action=open");
    let mentions;
    let editLink = false;
    let kindleLocateBtn;
    let showDeleteDialog = false;
    let isSticky = true;

    // FIXME: "shouldShowKindleModal" is never set
    // get from local storage should show kindle modal
    //$: shouldShowKindleModal = localStorage.getItem("shouldShowKindleModal") || true;

    $: isSelected =
        $SelectedSnippetsStore?.findIndex(
            (snippet) => snippet.id === card.id,
        ) !== -1;
    $: isSticky = cardContentWrapperHeight <= 400;
    $: sortedComments = card.comments?.sort((a, b) => {
        return a.createdOn - b.createdOn;
    });
    $: dropdownItems = [
        {
            label: `Add Title`,
            hide:
                (card?.name !== undefined &&
                    card?.name !== null &&
                    card?.name !== "") ||
                card.archived,
            action: async () => {
                cardTitleRef?.showTitle();
            },
            condition: editRole >= 3,
        },
        {
            label: `Add Tags`,
            hide: card.archived,
            action: () => {
                openSearch = true;
            },
            condition: true,
        },
        {
            label: `Copy Content`,
            action: handleCopyToClipboard,
            condition: true,
        },
        {
            label: "Edit Link",
            hide: isEditableUrl(card.url) === false || card.archived,
            action: () => {
                editLink = !editLink;
            },
            condition: editRole >= 3,
        },
        {
            label: card?.archived ? "Uncomplete" : "Mark as Done",
            action: () => {
                ActiveTopicStore.archiveSnippet(card, !card.archived);
            },
            condition: editRole >= 3,
        },
        {
            label: "Delete",
            action: () => {
                showDeleteDialog = true;
                isExpanded = false;
            },
            condition: editRole >= 3,
        },
    ];

    onMount(async () => {
        await verifySnippet();
    });

    const replyComment = (comment) => {
        isExpanded = false;
        showInput = true;

        const mentionJson = {
            id: comment.createdById,
            label:
                comment.createdBy?.firstName || comment.createdBy?.lastName
                    ? comment.createdBy?.firstName +
                      " " +
                      comment.createdBy?.lastName
                    : comment.createdBy?.username,
            username: comment.createdBy.username,
            isMember: true,
        };

        requestAnimationFrame(() => {
            editor.setReplyMention(mentionJson);
        });
    };

    const showKindlePopup = () => {
        downloadKindleShowModal = true;

        // remove modal after delay
        setTimeout(() => {
            downloadKindleShowModal = false;
        }, 3000);
    };

    const locate = async () => {
        // if we are locating a pdf then just redirect location to our pdf viewer
        // no need to call CustomLocate since we already have locate in [pdfLink].svelte
        if (
            ServiceConsts.isDashboardUrl(card.url) &&
            card.url.includes("/pdf/")
        ) {
            const locateInPdfUrl = new URL(card.url, window.location.origin);
            locateInPdfUrl.hash =
                ServiceConsts.SNIPPET_PARAM_NAME + "=" + card.id;

            if (
                removeUrlParameters(card.url) ===
                removeUrlParameters(window.location.href)
            ) {
                // if we're on mobile, close the extension
                if ($isMobile) {
                    ExtensionStore.setOpened(false);
                }

                history.replaceState({}, "", locateInPdfUrl.href);
            } else {
                window.open(card.url, "_blank");
            }
            return;
        }

        if (openInNewTab) {
            window.open(card.url, "_blank");

            // if we're on the same page scroll to snippet
        } else if (checkURLMatch(card.url, window.location.href)) {
            // if we're on mobile, close the extension
            if ($isMobile) {
                ExtensionStore.setOpened(false);
            }

            // if bookmark or social post scroll to top
            if (card.bookmark || isSocialMediaSnippet(card)) {
                window.scrollTo({ top: 0, behavior: "smooth" });

                // otherwise scroll to highlight
            } else {
                if ($PreferencesStore?.showAllHighlights) {
                    scrollToSnippet(card);
                } else {
                    highlightAndScrollToSnippet(card);
                }
            }

            // if we're on a different page redirect
        } else {
            /**
             Reddit's AbortController cancels fetch requests during navigation.
             Immediate redirects (location.href) interrupt JS execution before Reddits cleanup ends,
             causing "AbortError" exceptions in their code.

             location.replace() with a delay (via rAF) allows Reddit's cleanup to finish before navigation.
             This isn't about the extension reloading but about avoiding interference with Reddit's in-progress operations.

             Alternatives considered:
             * - queueMicrotask: not enough for Reddit's cross-frame cleanup.
             * - Direct location.href: terminates js execution too aggresively.
             * - window.history wont resolve this because pushState/replaceState does same thing.
             * - html element.click(): works but is not recommended.
             * - requestAnimationFrame: on double click locate will also break abort controller.
             * - setTimeout: does not work at all.
             * - debouncing whole locate function: works but its not a good practice.

             We need to use location.replace() Instead of window.location.href
             This prevents adding the redirection to the browser's history which triggers abort controller.

             When we're not on Reddit, we use window.location.href instead of location.replace
             to preserve the user's browser history and allow them to use the back button.
             */

            if (isRedditPage()) {
                location.replace(generateUrlFromSnippetForExtension(card));
            } else {
                window.location.href = generateUrlFromSnippetForExtension(card);
            }
        }

        await verifySnippet();

        clearUrlByMarker("#");
    };

    const kindleLocate = async (url) => {
        let focusout = false;
        kindleLocateBtn.addEventListener("focusout", () => {
            focusout = true;
        });

        Kindle.redirectAndLocateToKindle(url);

        setTimeout(() => {
            if (!focusout) {
                showKindlePopup();
            }
        }, 1000);
    };

    const addComment = async () => {
        if (trimHTMLText(commentInputValue) === "") return;

        let comment = {
            comment: commentInputValue,
            createdBy: $TokenStore?.user,
            mentionMembers: mentions.getMentionedMembers(),
            mentionAndCreateMembers: mentions.getCreateAndMentionMembers(),
            mentionNonMembers: mentions.getMentionedNonMembers(),
        };

        mentions.clear();
        showInput = false;
        commentInputValue = "";

        let createdComment = await createComment(
            comment,
            card.id,
            card.topic?.id,
        );

        addMentionedMembersToTenantCollaborators(createdComment);
    };

    const pressCommentBtn = () => {
        if (!card.id) return;

        if (trimHTMLText(commentInputValue) === "") {
            commentInputValue = trimHTMLText(commentInputValue);
            showInput = !showInput;
            return;
        }
        addComment();
    };

    const updateCommentAndAddCollaborators = async (comment) => {
        let updatedComment = await updateComment(comment, card.id, topicId);
        mentions.clear();
        addMentionedMembersToTenantCollaborators(updatedComment);
    };

    const addMentionedMembersToTenantCollaborators = (comment) => {
        if (comment.mentionAndCreateMembers)
            comment.mentionAndCreateMembers.forEach((member) => {
                TenantStore.addCollaborator(member.topicRole.user);
            });
    };

    const handleCopyToClipboard = () => {
        // if theres a title then copy title and add new line then copy content
        if (card.name) {
            copyToClip(
                "<br><h3>" + card.name + "</h3>" + card.content + "<br>",
            );
        } else {
            copyToClip("<br>" + card.content + "<br>");
        }
    };

    const deletionConfirmed = () => {
        showDeleteDialog = false;
        cleanupByHighlightId(card.id);
        HighlightStore.deleteHighlight(card.id);
        deleteSnippet(card.id, card.topic?.id);
    };

    function isEditableUrl(url) {
        if (
            url.includes("kindle://book/") ||
            ServiceConsts.isDashboardUrl(url)
        ) {
            return false;
        }
        return true;
    };

    const updateTitle = async (cardUpdate) => {
        try {
            await ActiveTopicStore.updateSnippet(cardUpdate);
        } catch (error) {
            console.error("Failed to update snippet:", error);
        }
    };

    export async function verifySnippet() {
        if (card && !card.verified) {
            let found = findSnippet(card);
            if (found) {
                card.verified = true;
                await ActiveTopicStore.updateSnippet({
                    id: card.id,
                    verified: true,
                });
            }
        }
    };

    const checkCardVerification = async () => {
        let attempts = 0;
        const maxAttempts = 10;
        const intervalTime = 500;

        const pollingInterval = setInterval(async () => {
            attempts++;
            const isVerified = await ActiveTopicStore.checkSnippetVerification(
                card.id,
            );
            if (isVerified === true) {
                card.verified = true;
                clearInterval(pollingInterval);
            } else if (attempts >= maxAttempts) {
                clearInterval(pollingInterval);
            }
        }, intervalTime);
    };

    const addTagToSnippet = async (newTag) => {
        await ActiveTopicStore.addTagToSnippet(newTag, card.id);
    };

    const removeTagFromSnippet = (tagToRemove) => {
        ActiveTopicStore.removeTagFromSnippet(tagToRemove, card.id);
    };

    const clearSnippetFocus = () => {
        // NOTE: Highlight target has animation that takes time to complete,
        //       so user has enough time to click to the next element to highlight,
        //       that's why we need to check whether the current element is
        //       still focused not toclear focus from the newly clicked element
        if ($focusedSnippetId === card?.id) {
            AppStore.setFocused(null);
            clearUrlByMarker("#");
        }
    };
</script>

<div
    class="inner-card-wrapper"
    class:selected-card={isSelected}
    class:inner-card-wrapper--disabled={$AppStore.snippetIsDragging === true &&
        $ActiveTopicStore.draggingSnippetId === card.id}
    class:inner_card_wrapper-padding={!isExtension && !isGridCard}
    class:inner-card-wrapper-grid={isGridCard}
    class:inner-card-wrapper--top={editLink}
>
    <span class="cw-highlight-web"></span>
    <span class="inner_card_wrapper-padding hidden"></span>

    {#key isCopilot === false && $focusedSnippetId === card?.id}
        <div
            class="card"
            class:sticky-card={isSticky}
            class:grid-card={isGridCard}
            data-snippet-id={card?.id}
            use:highlightTarget={{
                nodeId: card?.id,
                targetId: $focusedSnippetId,
                callback: clearSnippetFocus,
            }}
        >
            {#if downloadKindleShowModal && shouldShowKindleModal}
                <div class="card-kindle-prompt">
                    <p>
                        You need to have the Kindle app installed on your device
                        to open this link.
                    </p>
                    <button
                        on:click={() => {
                            downloadKindleShowModal = false;
                        }}
                    >
                        Close
                    </button>
                </div>
            {/if}

            <CardHeader
                {card}
                {editRole}
                {dropdownItems}
                {isGridCard}
                bind:editLink
            />

            <!-- span is a hack for node.insertBefore error on comment click -->
            <span
                class="z-1"
                class:card-content-wrapper-grid={isGridCard}
                bind:this={editorReferenceElement}
                data-reversesuggestionlist={(get(isMobile) ||
                    isExtensionOrPdfViewer()) &&
                    ordinal >= $ActiveTopic?.snippets.length - 2}
            >
                <div class="card-content-wrapper">
                    <Collapsible
                        bind:expanded={isExpanded}
                        hideExpandButton={isGridCard}
                        bind:cardContentWrapperHeight
                    >
                        <CardTitle
                            {card}
                            {editRole}
                            update={updateTitle}
                            bind:this={cardTitleRef}
                        />
                        <CardTags
                            {card}
                            {editRole}
                            bind:openSearch
                            addTag={addTagToSnippet}
                            removeTag={removeTagFromSnippet}
                        />
                        <CardAuthor {card} />
                        <CardContent {card} {isGridCard} bind:isExpanded />
                        <div slot="footer-content" class="card-creator-info">
                            <span
                                use:tooltip={card.createdBy?.firstName +
                                    " " +
                                    (card.createdBy?.lastName
                                        ? card.createdBy?.lastName
                                        : "") +
                                    " - " +
                                    formattedDateAndTime(card.createdOn)}
                            >
                                {card.createdBy?.firstName}
                                {card.createdBy?.lastName ?? ""} - {fromNow(
                                    card.createdOn,
                                )}
                            </span>
                        </div>
                    </Collapsible>
                </div>

                {#if showInput && editRole >= 2}
                    <div class="editor-wrapper">
                        <Editor
                            bind:this={editor}
                            bind:editorInputValue={commentInputValue}
                            bind:mentions
                            action={pressCommentBtn}
                            userRole={editRole}
                            parentWrapper={editorReferenceElement}
                        />
                    </div>
                {/if}
            </span>

            {#if !print}
                <div
                    class="cw-btn-container"
                    class:shadow={card.comments?.length > 0 && !isGridCard}
                >
                    {#if isCopilot}
                        <div class="copilot-reactions">
                            <div
                                use:clickAction={() => {
                                    card.copilotReactionValue = 1;
                                    addCopilotReaction(1, card);
                                }}
                            >
                                <svg
                                    class="copilot-reactions__btn"
                                    class:copilot-reactions__btn--active={card.copilotReactionValue ===
                                        1}
                                    class:copilot-reactions__btn--inactive={card.copilotReactionValue ===
                                        -1}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z"
                                    />
                                </svg>
                            </div>
                            <div
                                use:clickAction={() => {
                                    card.copilotReactionValue = -1;
                                    addCopilotReaction(-1, card);
                                }}
                            >
                                <svg
                                    class="copilot-reactions__btn"
                                    class:copilot-reactions__btn--active={card.copilotReactionValue ===
                                        -1}
                                    class:copilot-reactions__btn--inactive={card.copilotReactionValue ===
                                        1}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398C20.613 14.547 19.833 15 19 15h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384"
                                    />
                                </svg>
                            </div>
                        </div>
                    {/if}

                    {#if !hideReactions && !isCopilot}
                        <CardReactions snippet={card} />
                    {/if}

                    {#if card.url}
                        {#if isKindleUrl}
                            <button
                                class="btn right"
                                on:click={() => kindleLocate(card.url)}
                                bind:this={kindleLocateBtn}
                            >
                                Locate
                            </button>
                        {:else if isExtensionOrPdfViewer()}
                            <button
                                class="btn right"
                                use:disabled={{
                                    isDisabled: isPdfMissing(card),
                                    message: "PDF File deleted, cannot locate.",
                                }}
                                on:click={locate}
                                >Locate
                            </button>
                        {:else}
                            <a
                                class="btn right"
                                href={generateUrlFromSnippet(card)}
                                on:click={$AppStore.type ===
                                    AppType.EXTENSION && checkCardVerification}
                                target={openInNewTab ? "_blank" : null}
                                use:disabled={{
                                    isDisabled: isPdfMissing(card),
                                    message: "PDF File deleted, cannot locate.",
                                }}
                            >
                                Locate
                            </a>
                        {/if}
                    {/if}

                    {#if editRole >= 2 && !isGridCard}
                        <button class="btn" on:click={pressCommentBtn}>
                            {showInput && trimHTMLText(commentInputValue) === ""
                                ? "Cancel"
                                : mentions?.mentions.length > 0 &&
                                    mentions.mentions.every(
                                        (m) =>
                                            m.topicRole?.role ===
                                            TopicAccessLevel.MENTION,
                                    )
                                  ? "Comment"
                                  : editRole >= TopicAccessLevel.EDITOR &&
                                      mentions?.mentions.filter(
                                          (mention) => mention.topicRole,
                                      ).length
                                    ? "Comment & Share"
                                    : "Comment"}
                        </button>
                    {:else if editRole >= 1 && isGridCard}
                        <button
                            class="btn"
                            on:click={() => {
                                snippetSidePanelStore.setSnippet(card);
                                $AppStore.panel = Panels.SNIPPET;
                            }}
                            >Open
                        </button>
                    {/if}
                </div>
            {/if}

            <!-- MODALS -->
            {#if showDeleteDialog}
                <CardDialog
                    name={card.content
                        .replace(/<[^>]+>/g, "")
                        .substring(0, 30) + "..."}
                    hasComments={card.comments.length > 0}
                    confirmHandler={deletionConfirmed}
                    cancelHandler={() => {
                        showDeleteDialog = false;
                    }}
                    actionName="Delete"
                />
            {/if}
        </div>
    {/key}

    <!-- COMMENTS -->
    {#if sortedComments && sortedComments.length > 0 && !isGridCard}
        <div class="hider" style="background-color: {bgColor} !important"></div>
        <div
            class="comments-wrapper"
            class:comments-wrapper--top-padding-big-screens={!isExtension}
        >
            {#each sortedComments as comment, index (comment.id)}
                <!-- HACK: Key is needed for Search "Open Comment" to highlight comments on the currently visited Topic -->
                {#key $focusedCommentId === comment?.id}
                    <Comment
                        {comment}
                        {editRole}
                        {user}
                        reverseSuggestionList={true}
                        on:update={(event) =>
                            updateCommentAndAddCollaborators(event.detail)}
                        on:delete={() =>
                            deleteComment(comment, card.id, topicId)}
                        on:reply={() => {
                            replyComment(comment);
                        }}
                    />
                {/key}
            {/each}
        </div>
    {/if}
</div>

<style>
    @import "../../main.css";

    .inner-card-wrapper {
        @apply relative z-10 !important;
    }

    .inner-card-wrapper-grid {
        @apply h-full !important;
    }

    .inner-card-wrapper--disabled {
        @apply opacity-40 !important;
    }

    .inner_card_wrapper-padding {
        @apply pt-0 md:pt-4 !important;
    }

    .inner-card-wrapper--top {
        @apply z-20 !important;
    }

    .card {
        @apply reset z-50 flex select-text flex-col rounded-b-xl p-0 !important;
    }

    .card.grid-card {
        @apply h-full !important;
    }

    .card-kindle-prompt {
        @apply reset absolute bottom-8 z-[99] flex min-h-fit w-full flex-col bg-black text-xs text-white !important;
    }

    .card-kindle-prompt p {
        @apply reset px-4 pt-4 text-xs text-white !important;
    }

    .card-kindle-prompt button {
        @apply reset m-2 cursor-pointer self-end rounded-sm px-2 py-1 text-xs text-white hover:bg-gray-700 !important;
    }

    .sticky-card {
        @apply sticky top-4 !important;
    }

    .card-content-wrapper {
        @apply reset z-50 w-full min-w-full max-w-full border-x border-solid border-gray-200 bg-white pb-0 pt-1 text-sm !important;
    }

    .card-creator-info {
        @apply relative mt-auto flex justify-between text-xs text-gray-500 !important;
    }

    .card-creator-info span {
        @apply text-xs text-gray-500 !important;
    }

    .editor-wrapper {
        @apply reset relative z-40 min-w-full max-w-full border-x border-t border-solid border-gray-200 bg-white p-0 !important;
    }

    .hider {
        @apply sticky -top-1 z-[20] -mt-10 h-8 w-full bg-white !important;
    }

    .cw-btn-container {
        @apply m-0 flex select-none items-center rounded-b-xl border-[1px] border-solid border-gray-200 bg-white px-2 py-1 !important;
    }

    .cw-btn-container.shadow {
        @apply shadow-xl !important;
    }

    .btn {
        @apply reset rounded border-none px-2 py-1 text-xs font-normal text-black no-underline hover:bg-gray-200 focus:outline-none !important;
    }

    .btn:disabled {
        @apply cursor-not-allowed !important;
    }

    .btn::before {
        @apply reset content-none !important;
    }

    .btn:after {
        @apply reset content-none !important;
    }

    .btn.right {
        @apply ml-auto !important;
    }

    .comments-wrapper {
        @apply -mt-6 flex flex-col rounded-b-xl border-[1px] border-solid border-gray-200 bg-gray-100 pt-10 !important;
    }

    .comments-wrapper--top-padding-big-screens {
        @apply md:pt-10 !important;
    }

    .copilot-reactions {
        @apply reset flex flex-row items-center justify-between gap-2 px-2 py-1 !important;
    }

    .copilot-reactions__btn {
        @apply h-4 w-4 cursor-pointer text-gray-500 transition-all hover:scale-125 hover:text-gray-600 !important;
    }

    .copilot-reactions__btn--active {
        @apply text-gray-900 hover:scale-100 hover:text-gray-900 !important;
    }

    .copilot-reactions__btn--inactive {
        @apply text-gray-300 !important;
    }

    .selected-card > .card {
        @apply before:pointer-events-none before:absolute before:inset-0 before:z-20 before:rounded-xl before:border-2 before:border-blue-800 before:bg-sky-300 before:opacity-30 !important;
    }

    :global(.selected-card .card .card-header .dropdown) {
        @apply hidden !important;
    }

    .cw-highlight-web {
        background-color: #e7cbf7 !important;
    }

    .z-1 {
        z-index: 1;
    }
</style>